import gql from 'graphql-tag'
import React, { useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, BoxProps, Flex, FlexProps, Text } from 'rebass'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { oc } from 'ts-optchain'
import { styled } from '../../../styles/settings/theme'

import { Game, Map, Team } from '../../../types/graphql'
import { rawNumPlayers } from '../../../utils/sportUtils'
import { numToString } from '../../../utils/typeCoercions'
import { BaseButton } from '../../atoms/Buttons'
import LoadingSpinner from '../../atoms/LoadingSpinner'
import { CustomSelect } from '../../atoms/Select'
import SportLogo from '../../atoms/SportLogo'
import ValorantReportingTable from './ValorantReportingTable'
import { GameBoxReportEvidence, useEvidence } from './evidence'
import { currentUserIsAdmin } from '../../../utils/admins'

export const GET_VALORANT_AGENTS = gql`
  query ValorantAgents {
    sportPlayerCharacteristics(valorantAgents: true) {
      valorantAgents {
        options
      }
    }
  }
`
export const REPORT_VALORANT_GAME_MUTATION = gql`
  mutation report_valorant_game(
    $gameId: ID!
    $homeTeam: UserReportedGame!
    $awayTeam: UserReportedGame!
    $mapId: ID!
    $evidenceScreenshot: String!
  ) {
    reportValorantGame(
      gameId: $gameId
      homeTeam: $homeTeam
      awayTeam: $awayTeam
      mapId: $mapId
      evidenceScreenshot: $evidenceScreenshot
    ) {
      match {
        id
        settled
      }
    }
  }
`

interface GameProps {
  game: Game
  homeTeam: Team
  awayTeam: Team
  toOpen: any
  updateLoading: (loading: boolean) => void
  report: number
  index: number
  reportable: boolean
  maps: [Map] | null
  settled: boolean
}
const Circle = styled(Box)<BoxProps>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
`
const RowBox = styled(Flex)<FlexProps>`
  flex-direction: column;
  border: 2px solid ${props => props.theme.colors.darkmiddlegray};
  padding: 2rem 1.5rem;
  margin-top: 1.25rem;
`
const Reporting = styled(Flex)<FlexProps>`
  border-top: 2px solid ${props => props.theme.colors.lightgray};
`
interface Action {
  payload: { index: number; name: string; pick: string }
}

const teamReducer = (state: any, action: Action) => {
  return {
    ...state,
    [action.payload.index]: { name: action.payload.name, pick: action.payload.pick },
  }
}

const ValorantGameBox = ({
  game,
  homeTeam,
  awayTeam,
  toOpen,
  report,
  index,
  maps,
  reportable,
  updateLoading,
  settled,
}: GameProps) => {
  const [ready, setReady] = useState(false)
  const [mapName, setMapName] = useState(oc(maps)[0].name(''))
  const [homeTeamScore, setHomeTeamScore] = useState('0')
  const [awayTeamScore, setAwayTeamScore] = useState('0')
  const [valorantAgents, setValorantAgents] = useState([])
  const { evidenceScreenshotUrl, evidenceScreenshotName, handleEvidenceChanged } = useEvidence()

  const activeBox = report === index

  useQuery(GET_VALORANT_AGENTS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      setValorantAgents(data.sportPlayerCharacteristics.valorantAgents.options)
    },
  })

  const [reportValorantGame, { loading }] = useMutation(REPORT_VALORANT_GAME_MUTATION, {
    refetchQueries: ['matchDetailPage'],
    onError(_error) {},
    onCompleted(data) {
      if (data.reportValorantGame.match.settled) {
        toast.success(
          "Thank you for your submission. We're processing it now and brackets will be updated within 5 minutes",
          { containerId: 'temporary' },
        )
      } else {
        toast.success('Thank you for your submission!', { containerId: 'temporary' })
      }
      changeOpenIndex(-1)
      updateLoading(false)
    },
  })

  useEffect(() => {
    const isReadyForSubmit = () => {
      const someoneWon = parseInt(homeTeamScore) > 0 || Number(awayTeamScore) > 0
      return someoneWon && evidenceScreenshotUrl
    }
    setReady(isReadyForSubmit())

    if (activeBox) {
      loading ? updateLoading(true) : updateLoading(false)
    }
  }, [homeTeamScore, awayTeamScore, activeBox, loading, updateLoading, evidenceScreenshotUrl])

  const numPlayers = Number(rawNumPlayers(homeTeam.sportSlug))

  //create initial state object
  const results = (team: any) => {
    return {
      ...[...Array(numPlayers)].map((el, i) => {
        return {
          name: team.activeRosterEntries[i] ? team.activeRosterEntries[i].player.username : '',
          pick: team.activeRosterEntries[i]
            ? team.activeRosterEntries[i].player.characterName || valorantAgents[i]
            : '',
        }
      }),
    }
  }

  const homeTeamInitialState = results(homeTeam)
  const awayTeamInitialState = results(awayTeam)

  const [homeTeamState, homeDispatch] = useReducer(teamReducer, homeTeamInitialState)
  const [awayTeamState, awayDispatch] = useReducer(teamReducer, awayTeamInitialState)

  const handleChange = (e: any, isHomeTeam: any, i: any) => {
    const payload = {
      ...(isHomeTeam ? homeTeamState[i] : awayTeamState[i]),
      index: i,
      [e.target.name]: e.target.value,
    }
    isHomeTeam ? homeDispatch({ payload }) : awayDispatch({ payload })
  }

  const playerLookupId =
    homeTeam && homeTeam.activeRosterEntries && awayTeam && awayTeam.activeRosterEntries
      ? homeTeam.activeRosterEntries
          .concat(awayTeam.activeRosterEntries)
          .map((entry: any) => {
            return { name: entry.player.username, id: entry.player.id }
          })
          .reduce((obj: any, item: any) => {
            obj[item.name] = item.id
            return obj
          }, {})
      : null

  const mapsPresent = maps && maps.length > 0

  const mapLookupId =
    maps &&
    mapsPresent &&
    maps.reduce((obj: any, item: any) => {
      obj[item.name] = item.id
      return obj
    }, {})

  const changeOpenIndex = (index: number) => {
    toOpen(index)
  }

  const checkData = () => {
    //checks for duplicate names
    const homeNameObj: { [key: string]: boolean } = {}
    for (let key in homeTeamState) {
      if (!homeNameObj[homeTeamState[key].name]) {
        homeNameObj[homeTeamState[key].name] = true
      } else {
        toast.error(
          `You have entered duplicate players on ${homeTeam.name}. If there are not ${numPlayers} unique players on this team, the roster must be edited on the team's page before reporting this game.`,
          { containerId: 'temporary' },
        )
        return false
      }
    }

    const awayNameObj: { [key: string]: boolean } = {}
    for (let key in awayTeamState) {
      if (!awayNameObj[awayTeamState[key].name]) {
        awayNameObj[awayTeamState[key].name] = true
      } else {
        toast.error(
          `You have entered duplicate players on ${awayTeam.name}. If there are not ${numPlayers} unique players on this team, the roster must be edited on the team's page before reporting this game.`,
          { containerId: 'temporary' },
        )
        return false
      }
    }
    //checks for duplicate picks
    const checkPicks = (teamState: [{ [key: string]: string }]) => {
      const pickObj: { [key: string]: boolean } = {}
      const picks: Array<any> = Object.values(teamState)

      for (var i = 0; i < picks.length; i++) {
        if (!pickObj[picks[i].pick]) {
          pickObj[picks[i].pick] = true
        } else {
          toast.error('You have used ' + picks[i].pick + ' as a pick more than once.', {
            containerId: 'temporary',
          })
          return false
        }
      }
      return true
    }

    if (!checkPicks(homeTeamState)) return false
    if (!checkPicks(awayTeamState)) return false

    //checks for clear winner
    if (parseInt(homeTeamScore) < 13 && parseInt(awayTeamScore) < 13) {
      toast.error('One team must win at least 13 rounds. Please correct your scores.', {
        containerId: 'temporary',
      })
      return false
    }

    if (parseInt(homeTeamScore) > 13 || parseInt(awayTeamScore) > 13) {
      toast.error('A team cannot win more than 13 rounds. Please correct your scores.', {
        containerId: 'temporary',
      })
      return false
    }

    if (homeTeamScore === awayTeamScore) {
      toast.error('The score you have entered represents a tie. Please indicate a winner.', {
        containerId: 'temporary',
      })
      return false
    }

    return true
  }

  const handleSubmit = () => {
    const validData = checkData()
    if (validData) {
      const gamePayload = {
        id: game.id,
        homeTeam: {
          score: parseInt(homeTeamScore),
          userSubmittedLineup: Object.values(homeTeamState).map((entry: any) => {
            return { playerId: playerLookupId[entry.name], valorantChampionCd: entry.pick }
          }),
        },
        awayTeam: {
          score: parseInt(awayTeamScore),
          userSubmittedLineup: Object.values(awayTeamState).map((entry: any) => {
            return { playerId: playerLookupId[entry.name], valorantChampionCd: entry.pick }
          }),
        },
      }
      if (!loading) {
        reportValorantGame({
          variables: {
            gameId: gamePayload.id,
            homeTeam: gamePayload.homeTeam,
            awayTeam: gamePayload.awayTeam,
            mapId: mapLookupId && mapName ? mapLookupId[mapName] : '',
            evidenceScreenshot: evidenceScreenshotUrl, // is a  Base64 encoded data URL
          },
        })
      }
    }
  }

  return (
    <RowBox>
      {/* top row */}
      <Flex justifyContent="space-between" flexDirection={['column', 'column', 'row']}>
        <Flex alignItems="center" mb={[2, 2, 0]}>
          <Box mr={4}>
            <SportLogo sport={homeTeam.sportSlug} width="2rem" height="2rem" />
          </Box>
          <Text color="darkgray">
            <h3>Game {numToString(index + 1)}</h3>
          </Text>
          {game.winner && game.winner.team && (
            <Text color="green" ml={5} mt={'2px'}>
              <h5>{game.winner.team.truncatedName}</h5>
            </Text>
          )}
        </Flex>

        <Flex
          alignItems={['flex-start', 'flex-start', 'center']}
          width={[1, 1, 1 / 2]}
          justifyContent="flex-end"
          flexDirection={['column', 'column', 'row']}
        >
          {loading ? (
            <LoadingSpinner />
          ) : report !== index ? (
            <Flex mr={5} mb={[2, 2, 0]} alignItems="center">
              <Circle bg={game.winner ? 'green' : !settled ? 'red' : 'middlegray'} mr={2} />
              <Text color="darkgray" mt={'2px'}>
                <h6>
                  {game.winner
                    ? 'game reported'
                    : reportable
                    ? 'game unreported'
                    : settled
                    ? 'game not needed'
                    : 'report previous game'}
                </h6>
              </Text>
            </Flex>
          ) : maps && mapsPresent ? (
            <Box width={1 / 2} mb={[2, 2, 0]}>
              <CustomSelect
                key={index}
                value={mapName}
                onChange={e => setMapName(e.target.value)}
                mr={4}
              >
                {maps.map(item => (
                  <option key={item.id}>{item.name}</option>
                ))}
              </CustomSelect>
            </Box>
          ) : null}
          {((!game.winner && !loading && reportable && !settled) || currentUserIsAdmin()) && (
            <BaseButton
              variant={activeBox && !ready ? 'primaryDisabled' : 'primary'}
              disabled={(activeBox && !ready) || loading}
              onClick={activeBox ? handleSubmit : () => changeOpenIndex(index)}
            >
              {activeBox ? 'submit game report' : 'report game'}
            </BaseButton>
          )}
        </Flex>
      </Flex>
      {/* reporting content */}
      {activeBox && (
        <Reporting mt={5} flexWrap="wrap" mx={[0, 0, 0, -2]} p={1}>
          <ValorantReportingTable
            isHomeTeam={true}
            team={homeTeam}
            handleChange={handleChange}
            valorantAgents={valorantAgents}
            teamResults={homeTeamState}
            recordScore={setHomeTeamScore}
            score={homeTeamScore}
          />

          <ValorantReportingTable
            isHomeTeam={false}
            team={awayTeam}
            handleChange={handleChange}
            valorantAgents={valorantAgents}
            teamResults={awayTeamState}
            recordScore={setAwayTeamScore}
            score={awayTeamScore}
          />

          <GameBoxReportEvidence
            imageName={evidenceScreenshotName}
            imageUrl={evidenceScreenshotUrl}
            onImageChanged={handleEvidenceChanged}
          />
        </Reporting>
      )}
    </RowBox>
  )
}

export default ValorantGameBox
